import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Label,
  Input,
  Form,
  FormGroup,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "reactstrap";
import axios from "axios";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getToken } from "../../config/TokenHelper";

const BASE_URL = process.env.REACT_APP_DEFAULTAUTH + "/api/";

class ProfilePage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      user: null,
      firstName: "",
      lastName: "",
      email: "",
      mobile: "",
      loading: true,
      saving: false,
      success: "",
      error: "",
      passwordModal: false,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      passwordError: "",
      passwordSuccess: "",
      changingPassword: false,
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "My Profile", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadUserData();
  }

  loadUserData = () => {
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const user = JSON.parse(userStr);
        this.setState({
          user,
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
          mobile: user.mobile ? String(user.mobile) : "",
          loading: false,
        });
      } else {
        this.setState({ loading: false, error: "User data not found" });
      }
    } catch (e) {
      this.setState({ loading: false, error: "Error loading user data" });
    }
  };

  handleInputChange = (e) => {
    this.setState({ [e.target.name]: e.target.value, error: "", success: "" });
  };

  handleSubmit = async (e) => {
    e.preventDefault();
    this.setState({ saving: true, error: "", success: "" });

    const { authT, authk } = getToken();
    const { firstName, lastName, email, mobile, user } = this.state;

    try {
      const response = await axios.put(
        `${BASE_URL}team/update-profile`,
        { firstName, lastName, email, mobile: Number(mobile) },
        {
          headers: {
            authtoken: authT,
            authkey: authk,
          },
        }
      );

      if (response.data.success) {
        const updatedUser = { ...user, firstName, lastName, email, mobile: Number(mobile) };
        localStorage.setItem("user", JSON.stringify(updatedUser));

        this.setState({
          saving: false,
          success: "Profile updated successfully!",
          user: updatedUser,
        });
      } else {
        this.setState({
          saving: false,
          error: response.data.message || "Failed to update profile",
        });
      }
    } catch (err) {
      this.setState({
        saving: false,
        error: err.response?.data?.message || "Something went wrong",
      });
    }
  };

  togglePasswordModal = () => {
    this.setState((prev) => ({
      passwordModal: !prev.passwordModal,
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
      passwordError: "",
      passwordSuccess: "",
    }));
  };

  handlePasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = this.state;

    if (!currentPassword || !newPassword || !confirmPassword) {
      this.setState({ passwordError: "All fields are required" });
      return;
    }

    if (newPassword.length < 6) {
      this.setState({ passwordError: "New password must be at least 6 characters" });
      return;
    }

    if (newPassword !== confirmPassword) {
      this.setState({ passwordError: "New passwords do not match" });
      return;
    }

    this.setState({ changingPassword: true, passwordError: "" });

    const { authT, authk } = getToken();

    try {
      const response = await axios.put(
        `${BASE_URL}team/change-password`,
        { currentPassword, newPassword },
        {
          headers: {
            authtoken: authT,
            authkey: authk,
          },
        }
      );

      if (response.data.success) {
        this.setState({
          changingPassword: false,
          passwordSuccess: "Password changed successfully! You will be logged out.",
        });

        setTimeout(() => {
          localStorage.clear();
          window.location.href = "/login";
        }, 2000);
      } else {
        this.setState({
          changingPassword: false,
          passwordError: response.data.message || "Failed to change password",
        });
      }
    } catch (err) {
      this.setState({
        changingPassword: false,
        passwordError: err.response?.data?.message || "Something went wrong",
      });
    }
  };

  render() {
    const {
      loading,
      saving,
      firstName,
      lastName,
      email,
      mobile,
      success,
      error,
      user,
      passwordModal,
      currentPassword,
      newPassword,
      confirmPassword,
      passwordError,
      passwordSuccess,
      changingPassword,
    } = this.state;

    if (loading) {
      return (
        <div className="page-content">
          <Container fluid>
            <div className="text-center py-5">Loading...</div>
          </Container>
        </div>
      );
    }

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="My Profile" breadcrumbItems={this.state.breadcrumbItems} />

            <Row>
              <Col lg={8}>
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Profile Information</h4>

                    {success && <Alert color="success">{success}</Alert>}
                    {error && <Alert color="danger">{error}</Alert>}

                    <Form onSubmit={this.handleSubmit}>
                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>First Name</Label>
                            <Input
                              type="text"
                              name="firstName"
                              value={firstName}
                              onChange={this.handleInputChange}
                              placeholder="Enter first name"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Last Name</Label>
                            <Input
                              type="text"
                              name="lastName"
                              value={lastName}
                              onChange={this.handleInputChange}
                              placeholder="Enter last name"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <Row>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Email</Label>
                            <Input
                              type="email"
                              name="email"
                              value={email}
                              onChange={this.handleInputChange}
                              placeholder="Enter email"
                            />
                          </FormGroup>
                        </Col>
                        <Col md={6}>
                          <FormGroup>
                            <Label>Mobile</Label>
                            <Input
                              type="text"
                              name="mobile"
                              value={mobile}
                              onChange={this.handleInputChange}
                              placeholder="Enter mobile number"
                            />
                          </FormGroup>
                        </Col>
                      </Row>

                      <div className="d-flex justify-content-between mt-4">
                        <Button color="primary" type="submit" disabled={saving}>
                          {saving ? "Saving..." : "Save Changes"}
                        </Button>
                        <Button
                          color="secondary"
                          type="button"
                          onClick={this.togglePasswordModal}
                        >
                          <i className="ri-lock-line mr-1"></i>
                          Change Password
                        </Button>
                      </div>
                    </Form>
                  </CardBody>
                </Card>
              </Col>

              <Col lg={4}>
                <Card>
                  <CardBody className="text-center">
                    <div className="mb-3">
                      <div
                        className="avatar-lg mx-auto rounded-circle"
                        style={{
                          backgroundColor: "#556ee6",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: "80px",
                          height: "80px",
                        }}
                      >
                        <span className="text-white font-size-24">
                          {firstName?.charAt(0)?.toUpperCase() || "U"}
                          {lastName?.charAt(0)?.toUpperCase() || ""}
                        </span>
                      </div>
                    </div>
                    <h5 className="mb-1">
                      {firstName} {lastName}
                    </h5>
                    <p className="text-muted mb-2">{email}</p>
                    <span
                      className={`badge ${
                        user?.isSuperAdmin ? "badge-success" : "badge-info"
                      }`}
                    >
                      {user?.isSuperAdmin ? "Super Admin" : "Team Member"}
                    </span>

                    {user?.modulePermissions && user.modulePermissions.length > 0 && (
                      <div className="mt-4 text-left">
                        <h6 className="text-muted mb-2">Module Access:</h6>
                        <div>
                          {user.modulePermissions.map((module, idx) => (
                            <span
                              key={idx}
                              className="badge badge-soft-primary mr-1 mb-1"
                            >
                              {module}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>

        <Modal isOpen={passwordModal} toggle={this.togglePasswordModal}>
          <ModalHeader toggle={this.togglePasswordModal}>
            Change Password
          </ModalHeader>
          <ModalBody>
            {passwordError && <Alert color="danger">{passwordError}</Alert>}
            {passwordSuccess && <Alert color="success">{passwordSuccess}</Alert>}

            <FormGroup>
              <Label>Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) =>
                  this.setState({ currentPassword: e.target.value, passwordError: "" })
                }
                placeholder="Enter current password"
              />
            </FormGroup>

            <FormGroup>
              <Label>New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) =>
                  this.setState({ newPassword: e.target.value, passwordError: "" })
                }
                placeholder="Enter new password (min 6 characters)"
              />
            </FormGroup>

            <FormGroup>
              <Label>Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) =>
                  this.setState({ confirmPassword: e.target.value, passwordError: "" })
                }
                placeholder="Confirm new password"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={this.togglePasswordModal}>
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={this.handlePasswordChange}
              disabled={changingPassword}
            >
              {changingPassword ? "Changing..." : "Change Password"}
            </Button>
          </ModalFooter>
        </Modal>
      </React.Fragment>
    );
  }
}

export default ProfilePage;
